import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  getRefuelsCountData,
  getRefuelsData,
  getRefuelsDataByCaseId,
} from 'services/refuelServices';

import {
  InstallBaseData,
  getBaseTerritory,
  getInstallBases,
} from 'services/basesServices';

type CounterDataDTO = {
  total: number;
  statusOrderCount: {
    status: string;
    occurence: number;
  }[];
};

type AddressBase = {
  address: string;
  number: string;
  complement: string;
  addressCode: string;
  baseName: string;
};

type TrackingRefuelContextType = {
  isLoading: boolean;
  dataPages: any;
  dataDetail: any;
  totalPages?: number;
  currentPage?: number;
  totalPerPage?: number;
  activeTab: number;
  installBases: InstallBaseData[];
  startDate: Date | null;
  endDate: Date | null;
  counterData: CounterDataDTO;
  orderStatus: {
    eventDate: string;
    status: string;
    description?: string;
    completionType?: string;
    dateFormatted?: string;
    statusFormatted?: string;
  }[];
  addressBase: AddressBase;
  getRefuelsByCaseId: (caseId?: string, supplyId?: string) => void;
  handleChangeStartDate: (date: Date | null) => void;
  handleChangeEndDate: (date: Date | null) => void;
  nextPage: () => void;
  prevPage: () => void;
  handleSetPage: (page: number) => void;
  handleTotalPerPage: (total: number) => void;
  handleActiveTab: (tab: number) => void;
  handleSelectedBases: (base: InstallBaseData) => void;
  getStatusTypeNumber: (status: string) => {
    description: string;
    statusNumber: number;
  };
};

type TrackingProviderProps = {
  children: React.ReactNode;
};

export const TrackingRefuelContext = createContext<TrackingRefuelContextType>(
  {} as TrackingRefuelContextType
);

export const TrackingRefuelProvider: React.FC<TrackingProviderProps> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dataPages, setDataPages] = useState([]);
  const [dataDetail, setDataDetail] = useState([]);
  const [installBases, setInstallBases] = useState<InstallBaseData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPerPage, setTotalPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  const [requestParams, setRequestParams] = useState('');
  const [installBasesParam, setInstallBasesParam] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [orderStatus, setOrderStatus] = useState<
    { status: string; eventDate: string }[]
  >([]);
  const [counterData, setCounterData] = useState<CounterDataDTO>(
    {} as CounterDataDTO
  );
  const [addressBase, setAddressBase] = useState<AddressBase>({
    address: '',
    number: '',
    complement: '',
    addressCode: '',
    baseName: '',
  });

  const getBases = async (): Promise<void> => {
    setIsLoading(true);
    const response = await getInstallBases();

    const responseBases: InstallBaseData[] = await Promise.all(
      response?.map(async (base) => {
        const responseBaseTerritory = await getBaseTerritory(base.name);
        return {
          ...base,
          baseTerritory: responseBaseTerritory,
        };
      }) || ([] as InstallBaseData[])
    );

    setInstallBases(responseBases);

    setIsLoading(false);
  };

  function buildOrderStatus(
    status: string
  ): { status: string; eventDate: string }[] {
    let orderStatusResponse = [{ status: '', eventDate: '' }];
    switch (status) {
      case 'AGUCONLOG':
      case 'AGUTRA':
      case 'AGUANA':
      case 'GERPEDAUT':
      case 'RENDATCLI':
      case 'GREOSAUT':
      case 'ERRINT':
      case 'CANEA':
      case 'PEDIDO_EM_ANALISE':
        orderStatusResponse = [
          { status: 'RECEBIDO', eventDate: '' },
          { status: 'EM_ANALISE', eventDate: '' },
        ];
        break;
      case 'PEDAGE':
      case 'AGEREA':
      case 'AGENDADO':
        orderStatusResponse = [
          { status: 'RECEBIDO', eventDate: '' },
          { status: 'EM_ANALISE', eventDate: '' },
          { status: 'AGENDADO', eventDate: '' },
          { status: 'PREVISAO_ENTREGA', eventDate: '' },
        ];
        break;
      case 'REPROGRAMADO':
        orderStatusResponse = [
          { status: 'RECEBIDO', eventDate: '' },
          { status: 'EM_ANALISE', eventDate: '' },
          { status: 'RE_AGENDADO', eventDate: '' },
          { status: 'PREVISAO_ENTREGA', eventDate: '' },
        ];
        break;
      case 'CONFIRMADO':
        orderStatusResponse = [
          { status: 'RECEBIDO', eventDate: '' },
          { status: 'EM_ANALISE', eventDate: '' },
          { status: 'AGENDADO', eventDate: '' },
          { status: 'CONFIRMADO', eventDate: '' },
          { status: 'PREVISAO_ENTREGA', eventDate: '' },
        ];
        break;
      case 'EM_ROTA':
        orderStatusResponse = [
          { status: 'RECEBIDO', eventDate: '' },
          { status: 'EM_ANALISE', eventDate: '' },
          { status: 'AGENDADO', eventDate: '' },
          { status: 'CONFIRMADO', eventDate: '' },
          { status: 'PREVISAO_ENTREGA', eventDate: '' },
          { status: 'EM_ROTA', eventDate: '' },
        ];
        break;
      case 'NAO_ATENDIDO':
        orderStatusResponse = [
          { status: 'RECEBIDO', eventDate: '' },
          { status: 'EM_ANALISE', eventDate: '' },
          { status: 'AGENDADO', eventDate: '' },
          { status: 'CONFIRMADO', eventDate: '' },
          { status: 'PREVISAO_ENTREGA', eventDate: '' },
          { status: 'EM_ROTA', eventDate: '' },
          { status: 'NAO_ATENDIDO', eventDate: '' },
        ];
        break;
      case 'ATENDIDO':
        orderStatusResponse = [
          { status: 'RECEBIDO', eventDate: '' },
          { status: 'EM_ANALISE', eventDate: '' },
          { status: 'AGENDADO', eventDate: '' },
          { status: 'CONFIRMADO', eventDate: '' },
          { status: 'PREVISAO_ENTREGA', eventDate: '' },
          { status: 'EM_ROTA', eventDate: '' },
          { status: 'ATENDIDO', eventDate: '' },
        ];
        break;
      case 'NAO_IDENTIFICADO':
        orderStatusResponse = [
          { status: 'RECEBIDO', eventDate: '' },
          { status: 'EM_ANALISE', eventDate: '' },
          { status: 'AGENDADO', eventDate: '' },
          { status: 'CONFIRMADO', eventDate: '' },
          { status: 'PREVISAO_ENTREGA', eventDate: '' },
          { status: 'EM_ROTA', eventDate: '' },
          { status: 'NAO_IDENTIFICADO', eventDate: '' },
        ];
        break;
      default:
        orderStatusResponse = [
          { status: 'RECEBIDO', eventDate: '' },
          { status: 'EM_ANALISE', eventDate: '' },
        ];
        break;
    }
    return orderStatusResponse;
  }

  function getStatusTypeNumber(status: string): {
    description: string;
    statusNumber: number;
  } {
    let statusResponse = { description: '', statusNumber: 0 };
    switch (status) {
      case 'PEDAGE':
      case 'AGEREA':
        statusResponse = {
          description: 'Seu pedido foi agendado',
          statusNumber: 3,
        };
        break;
      case 'SNOREC':
      case 'CANREA':
      case 'ERRO':
      case 'CANCELADO':
        statusResponse = {
          description: 'Seu pedido está cancelado',
          statusNumber: 2,
        };
        break;
      case 'AGUCONLOG':
      case 'AGUTRA':
      case 'AGUANA':
      case 'GERPEDAUT':
      case 'RENDATCLI':
      case 'GREOSAUT':
      case 'ERRINT':
      case 'CANEA':
        statusResponse = {
          description: 'Seu pedido está em análise',
          statusNumber: 2,
        };
        break;
      case 'AGENDADO':
        statusResponse = {
          description: 'Seu pedido foi agendado',
          statusNumber: 3,
        };
        break;
      case 'REPROGRAMADO':
        statusResponse = {
          description: 'Seu pedido foi reagendado',
          statusNumber: 3.1,
        };
        break;
      case 'PEDIDO_EM_ANALISE':
        statusResponse = {
          description: 'Seu pedido está em análise',
          statusNumber: 2,
        };
        break;
      case 'CONFIRMADO':
        statusResponse = {
          description: 'Seu pedido foi confirmado',
          statusNumber: 4,
        };
        break;
      case 'EM_ROTA':
        statusResponse = {
          description: 'Seu pedido está em rota de entrega',
          statusNumber: 5,
        };
        break;
      case 'NAO_ATENDIDO':
        statusResponse = {
          description: 'Seu pedido não foi entregue',
          statusNumber: 6.1,
        };
        break;
      case 'ATENDIDO':
        statusResponse = {
          description: 'Seu pedido foi entregue',
          statusNumber: 6,
        };
        break;
      case 'NAO_IDENTIFICADO':
        statusResponse = {
          description: 'Seu pedido não foi identificado',
          statusNumber: 0,
        };
        break;
      default:
        statusResponse = {
          description: 'Seu pedido está em análise',
          statusNumber: 2,
        };
        break;
    }

    return statusResponse;
  }

  const getStatusFormatted = (status: string): string => {
    switch (status) {
      case 'RECEBIDO':
        return 'Seu pedido foi recebido';
      case 'EM_ANALISE':
        return 'Estamos analisando seu pedido';
      case 'AGENDADO':
        return 'Seu pedido foi agendado';
      case 'PREVISAO_ENTREGA':
        return 'Previsão de entrega:';
      case 'CONFIRMADO':
        return 'Seu pedido foi confirmado';
      case 'EM_ROTA':
        return 'Caminhão saiu para rota de entrega';
      case 'ATENDIDO':
        return 'Seu pedido foi atendido';
      case 'REPROGRAMADO':
      case 'RE_AGENDADO':
        return 'Seu pedido foi reagendado';
      case 'NAO_ATENDIDO':
        return 'Seu pedido não foi atendido';
      default:
        return 'Não identificado';
    }
  };

  const formaterOrderStatus = (
    events: any[]
  ): { status: string; eventDate: string }[] => {
    const groupedEvents = events.map((event) => {
      const { status } = event;
      event.statusFormatted = getStatusFormatted(status);
      return event;
    });

    return groupedEvents;
  };

  const getRefuels = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const responseRefuels = await getRefuelsData(
      currentPage,
      totalPerPage,
      requestParams,
      installBasesParam,
      startDate,
      endDate
    );

    const counterDataResponse = await getRefuelsCountData(
      currentPage,
      totalPerPage,
      installBasesParam,
      startDate,
      endDate
    );

    setCounterData(counterDataResponse);

    // @ts-ignore
    const formatedData = responseRefuels?.data?.map((item) => {
      const base = installBases.find(
        (installBase) =>
          installBase.name ===
          (item?.installBase ?? item?.requestedCase?.installBase)
      );

      return {
        ...item,
        installBaseName: base?.nickname,
      };
    });
    setDataPages(formatedData);
    setTotalPages(responseRefuels?.pageTotal);
    setIsLoading(false);
  }, [
    currentPage,
    totalPerPage,
    installBases,
    requestParams,
    installBasesParam,
    startDate,
    endDate,
  ]);

  const getRefuelsByCaseId = useCallback(
    async (caseId?: string, supplyId?: string): Promise<void> => {
      setIsLoading(true);
      // @ts-ignore
      let responseRefuels;

      if (caseId || supplyId) {
        responseRefuels = await getRefuelsDataByCaseId(caseId, supplyId);
      }

      const responseOrderStatus = buildOrderStatus(
        responseRefuels?.status?.code ?? responseRefuels?.status
      );

      setOrderStatus(
        formaterOrderStatus(responseOrderStatus) as {
          status: string;
          eventDate: string;
        }[]
      );

      const baseSelected = installBases.find(
        // @ts-ignore
        (installBase) => installBase.name === responseRefuels?.installBase
      );

      setAddressBase({
        address: baseSelected?.address ?? '',
        number: baseSelected?.number ?? '',
        complement: baseSelected?.complement ?? '',
        addressCode: baseSelected?.addressCode ?? '',
        baseName: baseSelected?.nickname ?? '',
      });

      // @ts-ignore
      setDataDetail(responseRefuels ?? {});
      setIsLoading(false);
    },
    [installBases]
  );

  const nextPage = (): void => {
    setCurrentPage((prev) => prev + 1);
  };

  const prevPage = (): void => {
    setCurrentPage((prev) => prev - 1);
  };

  const handleSetPage = (page: number): void => {
    setCurrentPage(page);
  };

  const handleActiveTab = (tab: number): void => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleTotalPerPage = (total: number): void => {
    setCurrentPage(1);
    setTotalPerPage(total);
  };

  const handleSelectedBases = (base: InstallBaseData): void => {
    const newInstallBases = installBases.map((installBase) => {
      if (installBase.name === base.name) {
        return {
          ...installBase,
          isSelected: !installBase.isSelected,
        };
      }
      return installBase;
    });

    setInstallBases([...newInstallBases]);
  };

  const handleChangeStartDate = (date: Date | null): void => {
    setStartDate(date);
  };

  const handleChangeEndDate = (date: Date | null): void => {
    setEndDate(date);
  };

  useEffect(() => {
    let requestParamsFilter = '';
    switch (activeTab) {
      case 2:
        requestParamsFilter += `&status-code=AGUANA, AGUCONLOG, ERRINT, GERPEDAUT, RENDATCLI, PEDIDO_EM_ANALISE, NOT_DELIVERED_MUTABLE, FECHAR, AGUTRA, GREOSAUT`;
        break;
      case 3:
        requestParamsFilter += `&status-code=PEDAGE, AGEREA, AGENDADO`;
        break;
      case 4:
        requestParamsFilter += `&status-code=CONFIRMADO`;
        break;
      case 5:
        requestParamsFilter += `&status-code=EM_ROTA`;
        break;
      case 6:
        requestParamsFilter += `&status-code=ATENDIDO`;
        break;
      case 7:
        requestParamsFilter += `&status-code=REPROGRAMADO`;
        break;
      case 8:
        requestParamsFilter += `&status-code=NAO_ATENDIDO`;
        break;
      default:
        requestParamsFilter += '';
        break;
    }
    setRequestParams(requestParamsFilter);
  }, [activeTab]);

  useEffect(() => {
    const installBasesSelected = installBases.filter((base) => base.isSelected);
    if (installBasesSelected.length === 0) {
      setInstallBasesParam('');
      return;
    }
    const basesSelected = installBasesSelected.map((base) => base.name);
    const basesSelectedString = basesSelected.join(', ');
    setInstallBasesParam(`&install-base=${basesSelectedString}`);
  }, [installBases]);

  useEffect(() => {
    getBases();
  }, []);

  useEffect(() => {
    getRefuels();
  }, [
    currentPage,
    totalPerPage,
    getRefuels,
    requestParams,
    installBasesParam,
    startDate,
    endDate,
  ]);

  return (
    <TrackingRefuelContext.Provider
      value={{
        isLoading,
        installBases,
        dataPages,
        dataDetail,
        counterData,
        orderStatus,
        getRefuelsByCaseId,
        nextPage,
        prevPage,
        handleSetPage,
        totalPages,
        currentPage,
        totalPerPage,
        startDate,
        endDate,
        handleChangeEndDate,
        handleChangeStartDate,
        handleTotalPerPage,
        handleActiveTab,
        handleSelectedBases,
        getStatusTypeNumber,
        activeTab,
        addressBase,
      }}
    >
      {children}
    </TrackingRefuelContext.Provider>
  );
};

export function useTrackingRefuel(): TrackingRefuelContextType {
  const context = useContext(
    TrackingRefuelContext
  ) as TrackingRefuelContextType;
  return context;
}
